/**
 * Avatar — Visual representation of a user, team, or entity.
 *
 * Avatar Component Guidelines
 * - Pass a `user` object (with `id`, optional `name`, optional `profileImageUrl`)
 *   to automatically resolve the image, initials, and a stable background color
 *   derived from the user's ID.
 * - Alternatively, use `imageSrc` / `name` / `icon` directly for non-user entities.
 * - Use `isSquare` for non-person entities like workspaces, brands, or apps — round
 *   avatars imply a person.
 * - Size based on context prominence: `xxs`/`xs` for inline mentions or dense lists,
 *   `sm`/`base` for standard list rows and cards, `lg`/`xl` for profile headers or
 *   detail views.
 * - When no `name` or `icon` is provided, a dotted-border placeholder renders — use
 *   this for empty/unassigned states (e.g. "add a member").
 * @see https://base-ui.com/react/components/avatar Base UI Avatar documentation
 * @see https://ui.shadcn.com/docs/components/avatar shadcn/ui Avatar documentation
 */
import type { DeprecatedLayoutClassName } from "../../types/deprecatedLayoutClassName";

import React from "react";

import { Avatar as AvatarPrimitive } from "@base-ui/react/avatar";
import { cva } from "class-variance-authority";

import twMerge from "../../utils/twMerge";
import { getUserAvatarColor } from "./avatarColors";

const avatarSizeVariants = cva("", {
  variants: {
    size: {
      xxs: "h-4 w-4",
      xs: "h-5 w-5",
      sm: "h-6 w-6",
      base: "h-9 w-9",
      lg: "h-12 w-12",
      xl: "h-24 w-24",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

const avatarFallbackTextVariants = cva("font-editorial italic", {
  variants: {
    size: {
      xxs: "text-[0.625rem]",
      xs: "text-xs",
      sm: "text-sm",
      base: "text-xl",
      lg: "text-2xl",
      xl: "text-5xl",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

type AvatarSize = "xxs" | "xs" | "sm" | "base" | "lg" | "xl";

type AvatarUser = {
  id: string;
  name?: string | null;
  profileImageUrl?: string | null;
};

type AvatarProps<ClassName extends string = string> = {
  user?: AvatarUser;
  name?: string | null;
  icon?: React.ReactNode;
  imageSrc?: string;
  className?: DeprecatedLayoutClassName<ClassName>;
  UNSAFE_className?: string;
  size?: AvatarSize;
  imageClassName?: string;
  isSquare?: boolean;
};

function Avatar<ClassName extends string>({
  user,
  imageSrc,
  name,
  icon,
  className,
  UNSAFE_className,
  isSquare,
  size = "base",
  imageClassName = "",
}: AvatarProps<ClassName>) {
  const resolvedName = user?.name ?? name;
  const resolvedImageSrc = user?.profileImageUrl ?? imageSrc;
  const avatarColor = user ? getUserAvatarColor(user.id) : undefined;

  const containerClassName = twMerge(
    "inline-flex items-center justify-items-center align-middle shrink-0 text-white",
    isSquare ? "rounded-xs" : "rounded-full",
    avatarSizeVariants({ size }),
    className,
    UNSAFE_className,
  );

  const firstLetter = resolvedName?.charAt(0) ?? "?";

  // Note (Yuxin, 2026-04-11): Always render the colored fallback behind the
  // image so removal is instant — no flash of empty state when the image URL
  // is cleared.
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={twMerge(containerClassName, "relative overflow-hidden")}
    >
      {resolvedName || icon ? (
        <div
          style={{
            borderRadius: "inherit",
            ...(avatarColor
              ? { backgroundColor: avatarColor, color: "white" }
              : {}),
          }}
          className={twMerge(
            !avatarColor && "bg-white text-default",
            "flex h-full w-full items-center justify-center",
            avatarFallbackTextVariants({ size }),
            imageClassName,
          )}
        >
          {icon ?? firstLetter?.toUpperCase()}
        </div>
      ) : (
        <div
          style={{ borderRadius: "inherit" }}
          className={twMerge(
            "border-border h-full w-full border-2 border-dotted",
            imageClassName,
          )}
        />
      )}
      {resolvedImageSrc && resolvedName && (
        <AvatarPrimitive.Image
          style={{ borderRadius: "inherit" }}
          className={twMerge(
            "absolute inset-0 h-full w-full object-cover",
            imageClassName,
          )}
          src={resolvedImageSrc}
          alt={resolvedName}
        />
      )}
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
